import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import TaskCard from "./TaskCard";

const BoardColumn = ({
  column,
  onAddCard,
  onOpenCard,
  onUpdateColumn,
  onDeleteColumn,
  canManage,
  canCreate = canManage,
  embedded = false,
  organizingColumns = false,
  cardsDragDisabled = false,
  columnDragHandleProps = null,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [adding, setAdding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [updatingColumn, setUpdatingColumn] = useState(false);
  const [renameValue, setRenameValue] = useState(column.name);
  const renameRef = useRef(null);

  const cardCount = (column.cards || []).length;
  const columnWidth = embedded ? 260 : 272;
  const baseColumnSurface = embedded
    ? theme.palette.background.paper
    : alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.78);
  const columnSurface = column.isDone && !embedded
    ? `linear-gradient(180deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${baseColumnSurface} 64px)`
    : column.isWaiting && !embedded
      ? `linear-gradient(180deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${baseColumnSurface} 64px)`
      : baseColumnSurface;

  const columnAccent = column.isDone
    ? theme.palette.success.main
    : column.isWaiting
      ? theme.palette.warning.main
      : theme.palette.primary.main;

  useEffect(() => {
    setRenameValue(column.name);
  }, [column.name]);

  useEffect(() => {
    if (renaming && renameRef.current) renameRef.current.focus();
  }, [renaming]);

  const submitCard = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onAddCard(column.id, title.trim());
      if (ok !== false) {
        setTitle("");
        setAdding(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitRename = async () => {
    const next = renameValue.trim();
    setRenaming(false);
    if (!next || next === column.name || updatingColumn) return;
    setUpdatingColumn(true);
    try {
      const ok = await onUpdateColumn?.(column.id, { name: next });
      if (ok === false) setRenameValue(column.name);
    } finally {
      setUpdatingColumn(false);
    }
  };

  const toggleDoneColumn = async () => {
    setMenuAnchor(null);
    if (updatingColumn) return;
    setUpdatingColumn(true);
    try {
      await onUpdateColumn?.(column.id, { isDone: !column.isDone });
    } finally {
      setUpdatingColumn(false);
    }
  };

  const toggleWaitingColumn = async () => {
    setMenuAnchor(null);
    if (updatingColumn) return;
    setUpdatingColumn(true);
    try {
      await onUpdateColumn?.(column.id, { isWaiting: !column.isWaiting });
    } finally {
      setUpdatingColumn(false);
    }
  };

  return (
    <Box
      sx={{
        width: columnWidth,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
      }}
    >
      <Box
        sx={{
          ...(String(columnSurface).startsWith("linear-gradient")
            ? { background: columnSurface }
            : { bgcolor: columnSurface }),
          borderRadius: embedded ? 2 : 2.5,
          px: embedded ? 1 : 1.25,
          pt: embedded ? 1 : 1.1,
          pb: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: embedded ? 280 : 0,
          transition: "box-shadow 0.2s ease, background-color 0.2s ease",
          "&:hover .column-menu-btn": { opacity: 1 },
          outline: organizingColumns
            ? `2px solid ${alpha(theme.palette.primary.main, 0.35)}`
            : "none",
          outlineOffset: -1,
          boxShadow: embedded
            ? `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}, inset 0 0 0 1px ${alpha(theme.palette.divider, 0.12)}`
            : theme.palette.mode === "dark"
              ? `inset 0 0 0 1px ${alpha(theme.palette.divider, 0.35)}`
              : `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}, inset 0 0 0 1px ${alpha(theme.palette.divider, 0.45)}`,
          "&:hover": {
            boxShadow: embedded
              ? undefined
              : theme.palette.mode === "dark"
                ? `inset 0 0 0 1px ${alpha(theme.palette.divider, 0.5)}`
                : `0 2px 8px ${alpha(theme.palette.common.black, 0.07)}, inset 0 0 0 1px ${alpha(theme.palette.divider, 0.55)}`,
          },
        }}
      >
        <Box
          sx={{
            height: 3,
            borderRadius: 1.5,
            mb: 1.1,
            mx: 0.25,
            bgcolor: alpha(columnAccent, column.isDone ? 0.7 : 0.45),
            flexShrink: 0,
          }}
        />
        {updatingColumn ? (
          <LinearProgress sx={{ mb: 1, height: 3, borderRadius: 2 }} />
        ) : null}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1, px: 0.25 }}>
          {organizingColumns && columnDragHandleProps ? (
            <Tooltip title={t("organizeColumnsDragHint")}>
              <IconButton
                size="small"
                {...columnDragHandleProps}
                sx={{
                  cursor: "grab",
                  color: "primary.main",
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {renaming ? (
            <TextField
              inputRef={renameRef}
              size="small"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={submitRename}
              disabled={updatingColumn}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitRename();
                if (e.key === "Escape") {
                  setRenameValue(column.name);
                  setRenaming(false);
                }
              }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper",
                  fontWeight: 700,
                },
              }}
            />
          ) : (
            <Typography
              variant="subtitle2"
              fontWeight={column.isDone ? 500 : 600}
              sx={{
                flex: 1,
                px: 0.5,
                lineHeight: 1.4,
                color: column.isDone ? "text.secondary" : "text.primary",
              }}
            >
              {column.name}
            </Typography>
          )}
          <Chip
            size="small"
            label={cardCount}
            sx={{
              height: 22,
              minWidth: 28,
              fontSize: "0.72rem",
              fontWeight: 600,
              bgcolor: column.isDone
                ? alpha(theme.palette.success.main, 0.1)
                : column.isWaiting
                  ? alpha(theme.palette.warning.main, 0.14)
                  : alpha(theme.palette.action.active, 0.06),
              color: column.isDone ? "success.main" : column.isWaiting ? "warning.dark" : "text.secondary",
            }}
          />
          {column.isDone && (
            <Tooltip title={t("doneColumnHint")}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
            </Tooltip>
          )}
          {column.isWaiting && !column.isDone && (
            <Tooltip title={t("waitingColumnHint")}>
              <HourglassEmptyOutlinedIcon sx={{ fontSize: 18, color: "warning.main" }} />
            </Tooltip>
          )}
          {canManage && (
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={updatingColumn}
              className="column-menu-btn"
              sx={{
                ml: -0.5,
                opacity: { xs: 1, sm: 0.45 },
                transition: "opacity 0.15s ease, background-color 0.15s ease",
                "&:hover": { opacity: 1, bgcolor: alpha(theme.palette.action.active, 0.08) },
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setRenaming(true);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("renameColumn")} />
          </MenuItem>
          <MenuItem onClick={toggleWaitingColumn}>
            <ListItemIcon>
              <HourglassEmptyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={column.isWaiting ? t("unsetWaitingColumn") : t("setWaitingColumn")}
              secondary={t("waitingColumnMenuHint")}
            />
          </MenuItem>
          <MenuItem onClick={toggleDoneColumn}>
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={column.isDone ? t("unsetDoneColumn") : t("setDoneColumn")}
              secondary={t("doneColumnMenuHint")}
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDeleteColumn?.(column);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary={t("deleteColumn")} />
          </MenuItem>
        </Menu>

        <Droppable
          droppableId={String(column.id)}
          type="CARD"
          isDropDisabled={organizingColumns || cardsDragDisabled}
        >
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 8,
                mx: -0.5,
                px: 0.5,
                // Espaço para hover (translateY + borda/sombra) não ser clipado no 1º card
                pt: 0.5,
                pb: 0.25,
                borderRadius: 1.5,
                bgcolor: snapshot.isDraggingOver
                  ? alpha(theme.palette.primary.main, 0.07)
                  : "transparent",
                outline: snapshot.isDraggingOver
                  ? `2px dashed ${alpha(theme.palette.primary.main, 0.35)}`
                  : "2px dashed transparent",
                outlineOffset: -2,
                transition: "background-color 0.2s ease, outline-color 0.2s ease",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha(theme.palette.text.primary, 0.12),
                  borderRadius: 999,
                },
              }}
            >
              {(column.cards || []).map((card, index) => (
                <Draggable
                  key={String(card.id)}
                  draggableId={String(card.id)}
                  index={index}
                  isDragDisabled={organizingColumns || cardsDragDisabled}
                >
                  {(dragProvided, dragSnapshot) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      sx={{
                        mb: column.isDone ? 0.75 : 1,
                        opacity: dragSnapshot.isDragging ? 0.98 : 1,
                        transform: dragSnapshot.isDragging ? "rotate(1.5deg) scale(1.02)" : "none",
                        filter: dragSnapshot.isDragging ? "drop-shadow(0 8px 16px rgba(15,23,42,0.18))" : "none",
                        transition: dragSnapshot.isDragging ? "none" : "opacity 0.15s, transform 0.15s",
                      }}
                    >
                      <TaskCard
                        card={card}
                        onOpen={onOpenCard}
                        dragHandleProps={dragProvided.dragHandleProps}
                        inDoneColumn={Boolean(column.isDone)}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {cardCount === 0 ? (
                <Box
                  sx={{
                    py: 2.25,
                    px: 1,
                    mx: 0.25,
                    mb: 0.5,
                    borderRadius: 1.5,
                    border: `1px dashed ${alpha(theme.palette.divider, 0.45)}`,
                    bgcolor: alpha(theme.palette.background.default, embedded ? 0.35 : 0.25),
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: "block", opacity: 0.85 }}>
                    {column.isDone ? t("channelBoardColumnDoneEmpty") : t("channelBoardColumnEmpty")}
                  </Typography>
                </Box>
              ) : null}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>

        {canCreate && (
          <Box sx={{ mt: 1 }}>
            {adding ? (
              <Stack spacing={1}>
                <TextField
                  size="small"
                  placeholder={t("cardTitlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={submitting}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitCard();
                    }
                    if (e.key === "Escape") {
                      setTitle("");
                      setAdding(false);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.paper",
                    },
                  }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={submitCard}
                    disabled={!title.trim() || submitting}
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{ fontWeight: 600, borderRadius: 1.5 }}
                  >
                    {submitting ? t("addingCard") : t("addCard")}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setTitle("");
                      setAdding(false);
                    }}
                    disabled={submitting}
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    {t("cancel")}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                  {t("addCardAssigneeHint")}
                </Typography>
              </Stack>
            ) : (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              fullWidth
              sx={{
                justifyContent: "flex-start",
                color: "text.secondary",
                fontWeight: 600,
                py: 0.85,
                borderRadius: 1.5,
                textTransform: "none",
                border: `1px dashed ${alpha(theme.palette.divider, 0.35)}`,
                transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  color: "primary.main",
                },
              }}
            >
              {t("addCardInline")}
            </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BoardColumn;
